import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { 
  ChevronRight, 
  PlusCircle, 
  Pencil as PencilIcon, 
  Sparkles, 
  Zap, 
  FileEdit, 
  Brain as BrainIcon, 
  Loader2,
  ArrowLeft,
  Calendar,
  FileText,
  Settings,
  LayoutIcon
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Skeleton } from "@/components/ui/skeleton"
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { TimeWithTimezone } from '@/components/timezone'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toast } from '@/components/ui/use-toast'
import ContentGallery from '@/components/content/ContentGallery'
import campaignBackgroundImage from '@/assets/images/campaign_background.png'

type Campaign = {
  id: string
  name: string
  status: string
  content: string | null
  call_to_action: string | null
  created_at: string
  originating_convergence_id: string | null
  brand_id: string
  campaign_type: "CONVERGENCE_BASED" | "BASE" | "CUSTOM"
  clicks: number
  views: number
  metadata?: Record<string, any> | null
  custom_idea?: string | null // For backward compatibility
  image_url?: string | null
}

type Convergence = {
  id: string
  trend: string
  name: string
}

type Content = any


export function CampaignDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [campaign, setCampaign] = useState<Campaign | null>(null)
  const [convergence, setConvergence] = useState<Convergence | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showEditThemeDialog, setShowEditThemeDialog] = useState(false)
  const [centralTheme, setCentralTheme] = useState('')
  const [savingTheme, setSavingTheme] = useState(false)
  const [generatingAutoContent, setGeneratingAutoContent] = useState(false)
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)
  const [enabledPlatforms, setEnabledPlatforms] = useState<Array<{
    platform: { name: string; type: string; id: string };
    autopost_enabled: boolean;
    autogenerate_enabled: boolean;
    status: string;
  }>>([])
  const [loadingPlatforms, setLoadingPlatforms] = useState(false)
  const [contents, setContents] = useState<Content[]>([])
  const [contentLoading, setContentLoading] = useState(true)
  const [contentCount, setContentCount] = useState(0)

  const fetchContent = async (campaignId: string) => {
    try {
      setContentLoading(true)
      const { data, error, count } = await supabase
        .from('contents')
        .select(`
          *,
          campaigns!inner(id, name)
        `, { count: 'exact' })
        .eq('campaign_id', campaignId)
        .in('status', ['READY', 'SCHEDULED', 'PUBLISHED', 'DRAFT', 'GENERATING', 'ARCHIVED'])
        .order('created_at', { ascending: false })
        .limit(12)

      if (error) throw error
      setContents(data || [])
      setContentCount(count || 0)
    } catch (err) {
      console.error('Error fetching content:', err)
      toast({
        title: "Error",
        description: "Failed to fetch campaign content",
        variant: "destructive",
      })
    } finally {
      setContentLoading(false)
    }
  }

  const fetchEnabledPlatforms = async (brandId: string) => {
    setLoadingPlatforms(true)
    try {
      const { data, error } = await supabase
        .from('platform_brand_settings')
        .select(`
          *,
          platform:platforms(*)
        `)
        .eq('brand_id', brandId)
        .eq('autopost_enabled', true)
        .eq('status', 'CONNECTED')

      if (error) throw error
      // Filter and transform the data to match our state type
      const transformedData = (data || [])
        .filter(item => item.platform) // Filter out any null platforms
        .map(item => ({
          platform: {
            name: item.platform!.name,
            type: item.platform!.type,
            id: item.platform!.id
          },
          autopost_enabled: item.autopost_enabled,
          autogenerate_enabled: item.autogenerate_enabled,
          status: item.status
        }))
      setEnabledPlatforms(transformedData)
    } catch (err) {
      console.error('Error fetching platforms:', err)
      toast({
        title: "Error",
        description: "Failed to fetch enabled platforms",
        variant: "destructive",
      })
    } finally {
      setLoadingPlatforms(false)
    }
  }

  useEffect(() => {
    async function fetchData() {
      try {
        if (!id) return

        const { data: campaignData, error: campaignError } = await supabase
          .from('campaigns')
          .select('*, image_url')
          .eq('id', id)
          .single()

        if (campaignError) throw campaignError
        setCampaign(campaignData as Campaign)

        // Fetch content for this campaign
        await fetchContent(id)

        // If it's a convergence-based campaign, fetch the convergence data
        if (campaignData.campaign_type === 'CONVERGENCE_BASED' && campaignData.originating_convergence_id) {
          const { data: convergenceData, error: convergenceError } = await supabase
            .from('convergences')
            .select('id, trend, name')
            .eq('id', campaignData.originating_convergence_id)
            .single()

          if (convergenceError) throw convergenceError
          setConvergence(convergenceData)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch campaign')
      } finally {
        setLoading(false)
      }
    }

    fetchData()

    // Set up real-time subscriptions
    const campaignSubscription = supabase
      .channel(`campaign_${id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'campaigns',
        filter: `id=eq.${id}`
      }, payload => {
        setCampaign(payload.new as Campaign)
      })
      .subscribe()

    const contentSubscription = supabase
      .channel(`content_${id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'contents',
        filter: `campaign_id=eq.${id}`
      }, () => {
        if (id) fetchContent(id)
      })
      .subscribe()

    return () => {
      campaignSubscription.unsubscribe()
      contentSubscription.unsubscribe()
    }
  }, [id])

  useEffect(() => {
    if (campaign && campaign.campaign_type === 'CUSTOM') {
      // Get the central theme from metadata, or fallback to custom_idea for backward compatibility
      const theme = campaign.metadata?.central_theme || campaign.custom_idea || '';
      setCentralTheme(theme);
    }
  }, [campaign]);

  const handleCreateContent = () => {
    navigate('/content/create', { state: { campaignId: id } })
  }

  const handleShowGenerateDialog = async () => {
    if (!campaign?.brand_id) return;
    await fetchEnabledPlatforms(campaign.brand_id);
    setShowGenerateDialog(true);
  };

  const handleGenerateAutoContent = async () => {
    if (!campaign || !id) return;
    setShowGenerateDialog(false);
    setGeneratingAutoContent(true);
    try {
      // Get session for authentication
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No authentication session found');
      }

      const workerUrl = import.meta.env.VITE_WORKERS_URL;
      const response = await fetch(`${workerUrl}/rainfactory/manual-autoposter/generate-autopost-content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          campaignId: id,
          convergenceId: campaign.originating_convergence_id
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      if (result.success) {
        toast({
          title: "Content Generated Successfully!",
          description: result.message,
        });
        
        // Refresh the content list to show new content
        if (id) await fetchContent(id)
      } else {
        throw new Error(result.error || 'Content generation failed');
      }

    } catch (error) {
      console.error('Error generating auto content:', error);
      toast({
        title: "Generation Failed",
        description: error instanceof Error ? error.message : 'Failed to generate content',
        variant: "destructive",
      });
    } finally {
      setGeneratingAutoContent(false);
    }
  };

  const handleSaveCentralTheme = async () => {
    if (!campaign) return;
    
    setSavingTheme(true);
    try {
      const metadata = { 
        ...campaign.metadata,
        central_theme: centralTheme 
      };
      
      const { error } = await supabase
        .from('campaigns')
        .update({ metadata })
        .eq('id', campaign.id);
      
      if (error) throw error;
      
      // Update local campaign state with new metadata
      setCampaign({
        ...campaign,
        metadata
      });
      
      toast({
        title: "Theme updated",
        description: "The campaign theme has been updated successfully.",
      });
      
      setShowEditThemeDialog(false);
    } catch (err) {
      console.error('Error updating theme:', err);
      toast({
        title: "Update failed",
        description: "There was a problem updating the campaign theme.",
        variant: "destructive",
      });
    } finally {
      setSavingTheme(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        <div className="container mx-auto p-6 space-y-6">
          <Skeleton className="h-10 w-32" />
          <Skeleton className="h-48 w-full rounded-2xl" />
          <Skeleton className="h-32 w-full rounded-xl" />
          <Skeleton className="h-96 w-full rounded-xl" />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        <div className="container mx-auto p-6">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/campaigns')}
            className="group mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
            Back to Campaigns
          </Button>
          <Card className="bg-white dark:bg-gray-800 shadow-xl border-0">
            <CardContent className="p-8 text-center">
              <div className="text-red-500 mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold mb-2">Error Loading Campaign</h2>
              <p className="text-muted-foreground">{error}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (!campaign) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
        <div className="container mx-auto p-6">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/campaigns')}
            className="group mb-6"
          >
            <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
            Back to Campaigns
          </Button>
          <Card className="bg-white dark:bg-gray-800 shadow-xl border-0">
            <CardContent className="p-8 text-center">
              <div className="text-muted-foreground mb-4">
                <svg className="w-12 h-12 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-lg font-semibold mb-2">Campaign Not Found</h2>
              <p className="text-muted-foreground">The campaign you're looking for doesn't exist.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  // Helper functions for styling
  const getCampaignGradient = (campaign: Campaign) => {
    switch (campaign.campaign_type) {
      case 'CONVERGENCE_BASED':
        return 'from-purple-500 to-violet-600'
      case 'CUSTOM':
        return 'from-orange-500 to-red-600'
      case 'BASE':
        return 'from-blue-500 to-indigo-600'
      default:
        return 'from-gray-500 to-slate-600'
    }
  }

  const getCampaignBgGradient = (campaign: Campaign) => {
    switch (campaign.campaign_type) {
      case 'CONVERGENCE_BASED':
        return 'from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20'
      case 'CUSTOM':
        return 'from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20'
      case 'BASE':
        return 'from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20'
      default:
        return 'from-gray-50 to-slate-50 dark:from-gray-950/20 dark:to-slate-950/20'
    }
  }

  const getCampaignIcon = (campaign: Campaign) => {
    switch (campaign.campaign_type) {
      case 'CONVERGENCE_BASED':
        return <Sparkles className="h-5 w-5 text-white" />
      case 'CUSTOM':
        return <Zap className="h-5 w-5 text-white" />
      case 'BASE':
        return <BrainIcon className="h-5 w-5 text-white" />
      default:
        return <LayoutIcon className="h-5 w-5 text-white" />
    }
  }

  const getCampaignTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      'CONVERGENCE_BASED': 'Convergence Campaign',
      'BASE': 'Brand Campaign',
      'CUSTOM': 'Custom Campaign'
    }
    return labels[type] || type
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-900">
      <div className="container mx-auto p-4 sm:p-6 space-y-4 sm:space-y-6">
        {/* Back Navigation */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/campaigns')}
            className="group"
          >
            <ArrowLeft className="h-4 w-4 mr-2 transition-transform group-hover:-translate-x-1" />
            Back to Campaigns
          </Button>
        </div>

        {/* Hero Header - Mobile Optimized */}
        <div className={`relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-r ${getCampaignBgGradient(campaign)} shadow-lg border`}>
          {/* Background Image */}
          <div className="absolute inset-0">
            <img 
              src={campaign.image_url || campaignBackgroundImage} 
              alt={`${campaign.name} campaign background`}
              className="w-full h-full object-cover opacity-20"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/40 to-black/20" />
          </div>
          <div className="absolute top-0 right-0 w-48 sm:w-96 h-48 sm:h-96 bg-gradient-to-br ${getCampaignGradient(campaign)} rounded-full blur-3xl opacity-10" />
          
          <div className="relative z-10 p-4 sm:p-6 lg:p-8">
            {/* Mobile Layout */}
            <div className="sm:hidden space-y-4">
              {/* Header with icon and title */}
              <div className="flex items-start gap-3">
                <div className={`w-12 h-12 bg-gradient-to-br ${getCampaignGradient(campaign)} rounded-xl flex items-center justify-center shadow-xl flex-shrink-0`}>
                  {getCampaignIcon(campaign)}
                </div>
                <div className="flex-1 min-w-0">
                  <h1 className="text-2xl font-bold text-white drop-shadow-lg truncate">
                    {campaign.name}
                  </h1>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <Badge variant="outline" className="font-medium text-xs">
                      {getCampaignTypeLabel(campaign.campaign_type)}
                    </Badge>
                    <Badge 
                      variant={campaign.status === 'Active' ? 'default' : 'secondary'}
                      className={campaign.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 text-xs' : 'text-xs'}
                    >
                      {campaign.status}
                    </Badge>
                  </div>
                </div>
              </div>
              
              {/* Edit button - full width on mobile */}
              <Button 
                onClick={() => navigate(`/campaigns/${id}/edit`)}
                className="w-full shadow-lg"
                size="sm"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Campaign
              </Button>
            </div>

            {/* Desktop Layout */}
            <div className="hidden sm:flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 bg-gradient-to-br ${getCampaignGradient(campaign)} rounded-xl flex items-center justify-center shadow-xl`}>
                  {getCampaignIcon(campaign)}
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-white drop-shadow-lg">
                    {campaign.name}
                  </h1>
                  <div className="flex items-center gap-3 mt-2">
                    <Badge variant="outline" className="font-medium">
                      {getCampaignTypeLabel(campaign.campaign_type)}
                    </Badge>
                    <Badge 
                      variant={campaign.status === 'Active' ? 'default' : 'secondary'}
                      className={campaign.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : ''}
                    >
                      {campaign.status}
                    </Badge>
                  </div>
                </div>
              </div>
              
              <Button 
                onClick={() => navigate(`/campaigns/${id}/edit`)}
                className="shadow-lg"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit Campaign
              </Button>
            </div>

            {/* Stats Cards - Mobile optimized */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mt-4 sm:mt-6">
              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Calendar className="h-3.5 sm:h-4 w-3.5 sm:w-4" />
                  <span className="text-xs sm:text-sm font-medium">Created</span>
                </div>
                <TimeWithTimezone 
                  date={campaign.created_at} 
                  dateOnly={false}
                  size="xs"
                  showTimezone={false}
                  showRelative={true}
                  preferRelative={true}
                />
              </div>
              
              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <FileText className="h-3.5 sm:h-4 w-3.5 sm:w-4" />
                  <span className="text-xs sm:text-sm font-medium">Content Pieces</span>
                </div>
                <p className="text-base sm:text-lg font-semibold">{contentLoading ? '-' : contentCount}</p>
              </div>
              
              <div className="bg-white/50 dark:bg-gray-800/50 backdrop-blur rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-1">
                  <Settings className="h-3.5 sm:h-4 w-3.5 sm:w-4" />
                  <span className="text-xs sm:text-sm font-medium">Performance</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                  <div className="text-xs sm:text-sm">
                    <span className="font-semibold">{campaign.views || 0}</span> views
                  </div>
                  <div className="text-xs sm:text-sm">
                    <span className="font-semibold">{campaign.clicks || 0}</span> clicks
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Campaign Details Card - Mobile Optimized */}
        <Card className="bg-white dark:bg-gray-800 shadow-xl border-0 overflow-hidden">
          <div className={`h-2 bg-gradient-to-r ${getCampaignGradient(campaign)}`} />
          <CardContent className="p-4 sm:p-6">
            {campaign.campaign_type === 'CUSTOM' && (
              <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-950/20 dark:to-red-950/20 rounded-lg p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:mb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="h-4 sm:h-5 w-4 sm:w-5 text-orange-600 dark:text-orange-400" />
                    <h3 className="text-sm sm:text-base font-semibold">Central Theme</h3>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setShowEditThemeDialog(true)}
                    className="hover:bg-white/50 w-full sm:w-auto"
                  >
                    <FileEdit className="h-4 w-4 mr-1" /> Edit Theme
                  </Button>
                </div>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 leading-relaxed mt-2 sm:mt-0">
                  {campaign.metadata?.central_theme || campaign.custom_idea || 'No theme specified'}
                </p>
              </div>
            )}
            
            {campaign.campaign_type === 'CONVERGENCE_BASED' && convergence && (
              <div className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-950/20 dark:to-violet-950/20 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <Sparkles className="h-4 sm:h-5 w-4 sm:w-5 text-purple-600 dark:text-purple-400" />
                  <h3 className="text-sm sm:text-base font-semibold">Based on Convergence</h3>
                </div>
                <Link 
                  to={`/convergences/${convergence.id}`}
                  className="inline-flex items-center gap-2 text-purple-700 dark:text-purple-300 hover:text-purple-900 dark:hover:text-purple-100 transition-colors group"
                >
                  <span className="font-medium text-xs sm:text-sm group-hover:underline">{convergence.trend}</span>
                  <ChevronRight className="h-3 sm:h-4 w-3 sm:w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            )}
            
            {campaign.campaign_type === 'BASE' && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-lg p-3 sm:p-4">
                <div className="flex items-center gap-2 mb-2 sm:mb-3">
                  <BrainIcon className="h-4 sm:h-5 w-4 sm:w-5 text-blue-600 dark:text-blue-400" />
                  <h3 className="text-sm sm:text-base font-semibold">Brand Campaign</h3>
                </div>
                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-2 sm:mb-3">
                  This campaign is built on your brand's strategy and positioning. Use Brand Genius to shape and control the content.
                </p>
                <Link 
                  to="/brand-genius"
                  className="inline-flex items-center gap-2 text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 transition-colors group"
                >
                  <span className="font-medium text-xs sm:text-sm group-hover:underline">Visit Brand Genius</span>
                  <ChevronRight className="h-3 sm:h-4 w-3 sm:w-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons - Mobile Optimized */}
        <div className="flex justify-center px-4 sm:px-0">
          <div className="w-full sm:w-auto sm:inline-flex flex flex-col sm:flex-row gap-3 sm:gap-4 p-4 sm:p-6 bg-white dark:bg-gray-800 rounded-xl shadow-xl">
            <Button 
              onClick={handleCreateContent} 
              size="default"
              className="w-full sm:w-auto bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all"
            >
              <PlusCircle className="h-4 sm:h-5 w-4 sm:w-5 mr-2" />
              Create Content
            </Button>
            
            {campaign.campaign_type === 'CONVERGENCE_BASED' && campaign.originating_convergence_id && (
              <Button 
                onClick={handleShowGenerateDialog} 
                size="default"
                variant="outline"
                disabled={generatingAutoContent}
                className="w-full sm:w-auto border-2 border-indigo-500 text-indigo-600 hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-600 hover:text-white hover:border-transparent shadow-lg hover:shadow-xl transition-all"
              >
                {generatingAutoContent ? (
                  <Loader2 className="h-4 sm:h-5 w-4 sm:w-5 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 sm:h-5 w-4 sm:w-5 mr-2" />
                )}
                {generatingAutoContent ? 'Generating...' : 'Generate Content'}
              </Button>
            )}
          </div>
        </div>

        {/* Created Content Section */}
        {(contents.length > 0 || contentLoading) && (
          <Card className="bg-white dark:bg-gray-800 shadow-xl border-0 overflow-hidden">
            <div className="h-2 bg-gradient-to-r from-blue-500 via-indigo-600 to-purple-600" />
            <CardHeader className="border-b bg-gradient-to-r from-blue-50/30 via-indigo-50/20 to-purple-50/30 dark:from-blue-950/20 dark:via-indigo-950/15 dark:to-purple-950/20">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Campaign Content
                {contentCount > 0 && (
                  <Badge variant="secondary" className="ml-2">
                    {contentCount}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <ContentGallery
                contents={contents}
                loading={contentLoading}
                onContentClick={(content) => navigate(`/content/${content.id}`)}
                emptyMessage="Create your first piece of content for this campaign to get started."
              />
            </CardContent>
          </Card>
        )}

      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Campaign Content</DialogTitle>
            <DialogDescription>
              Review what will happen when you generate content for this campaign.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {loadingPlatforms ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : (
              <>
                <div>
                  <h3 className="font-medium mb-2">1. Content Generation</h3>
                  {enabledPlatforms.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">
                        Platform status overview:
                      </p>
                      <div className="space-y-4 mt-2">
                        <div className="space-y-3">
                          {enabledPlatforms.map(p => (
                            <div key={p.platform.id} className="flex flex-col space-y-1">
                              <h4 className="text-sm font-medium">{p.platform.name}</h4>
                              <div className="flex gap-4 text-xs">
                                <div className="flex items-center gap-1.5">
                                  <div className={`w-2 h-2 rounded-full ${p.autogenerate_enabled ? 'bg-green-500' : 'bg-yellow-500'}`} />
                                  <span className="text-muted-foreground">
                                    Auto-creation: {p.autogenerate_enabled ? 'Enabled' : 'Disabled'}
                                  </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <div className={`w-2 h-2 rounded-full ${p.autopost_enabled ? 'bg-green-500' : 'bg-yellow-500'}`} />
                                  <span className="text-muted-foreground">
                                    Auto-publishing: {p.autopost_enabled ? 'Enabled' : 'Disabled'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        <div className="pt-2 border-t">
                          <Link
                            to={`/brands/${campaign.brand_id}/connections`}
                            className="text-sm text-primary hover:underline inline-flex items-center"
                          >
                            <PencilIcon className="h-4 w-4 mr-1" />
                            Adjust platform settings
                          </Link>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <p className="text-sm text-yellow-600">
                        No platforms are currently enabled for both auto-creation and auto-publishing.
                      </p>
                      <Link
                        to={`/brands/${campaign.brand_id}/connections`}
                        className="text-sm text-primary hover:underline inline-flex items-center"
                      >
                        <PencilIcon className="h-4 w-4 mr-1" />
                        Configure platform settings
                      </Link>
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-medium mb-2">2. Content Scheduling</h3>
                  <p className="text-sm text-muted-foreground">
                    Generated content will be automatically scheduled based on your platform settings.
                  </p>
                </div>

                <div>
                  <h3 className="font-medium mb-2">3. Email Notification</h3>
                  <p className="text-sm text-muted-foreground">
                    You'll receive an email with:
                  </p>
                  <ul className="list-disc list-inside text-sm pl-2 text-muted-foreground">
                    <li>Preview of all generated content</li>
                    <li>Scheduled posting times</li>
                    <li>Instructions for reviewing and editing content</li>
                  </ul>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerateAutoContent}
              disabled={loadingPlatforms || enabledPlatforms.length === 0}
            >
              Generate Content
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showEditThemeDialog} onOpenChange={setShowEditThemeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Campaign Theme</DialogTitle>
            <DialogDescription>
              Update the central theme for this custom campaign.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="centralTheme">Central Theme</Label>
              <Textarea
                id="centralTheme"
                value={centralTheme}
                onChange={(e) => setCentralTheme(e.target.value)}
                placeholder="Describe the central theme or idea for this campaign"
                className="min-h-[100px]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditThemeDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCentralTheme} disabled={savingTheme}>
              {savingTheme ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  )
}
